import { KirtanLyricsdata } from "~/interface/kirtan-lyrics/kirtan-lyrics";

export const dbName = "MyDatabase";
export const dbVersion = 1;

// Example store names
export const storeNames = {
  kirtanLyrics: "KirtanLyricsdata",
  kirtanSyncDate: "KirtanSyncDate",
};

const IndexedDBService = {
  db: null as IDBDatabase | null,

  // Initialize the IndexedDB database
  initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, dbVersion);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBRequest).result;

        // Create object stores
        db.createObjectStore(storeNames.kirtanLyrics, { keyPath: "i" });
        db.createObjectStore(storeNames.kirtanSyncDate, { keyPath: "id" }); // Example for another store
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBRequest).result;
        resolve();
      };

      request.onerror = (event) => {
        reject(`IndexedDB initialization error: ${(event.target as IDBRequest).error}`);
      };
    });
  },

  // Add an item to any object store
  addItem(item: KirtanLyricsdata | { id: number; syncDate: string }, storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("Database not initialized");
        return;
      }

      const transaction = this.db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.add(item);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error adding item: ${(event.target as IDBRequest).error}`);
    });
  },

  // Get a single item from any object store by its id
  getData(key: number, storeName: string): Promise<KirtanLyricsdata | { id: number; syncDate: string } | undefined> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("Database not initialized");
        return;
      }

      const transaction = this.db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = (event) => {
        resolve((event.target as IDBRequest).result);
      };

      request.onerror = (event) => {
        reject(`Error retrieving data: ${(event.target as IDBRequest).error}`);
      };
    });
  },

  // Get all data from any object store
  getAllData(storeName: string): Promise<KirtanLyricsdata[] | { id: number; syncDate: string }[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("Database not initialized");
        return;
      }

      const transaction = this.db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.openCursor();
      const allData: KirtanLyricsdata[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          allData.push(cursor.value);
          cursor.continue();
        } else {
          resolve(allData);
        }
      };

      request.onerror = (event) => {
        reject(`Error fetching all data: ${(event.target as IDBRequest).error}`);
      };
    });
  },

  // Update an existing item in any object store
  updateItem(item: KirtanLyricsdata | { id: number; syncDate: string }, storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("Database not initialized");
        return;
      }

      const transaction = this.db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.put(item);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error updating item: ${(event.target as IDBRequest).error}`);
    });
  },

  // Get filtered data based on raag_dhal, bhav, and rachayta filters
  async getFilteredData(storeName: string, raagDhalFilter: number[], bhavFilter: number[], rachaytaFilter: number[]): Promise<KirtanLyricsdata[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("Database not initialized");
        return;
      }

      const transaction = this.db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.openCursor();
      const filteredData: KirtanLyricsdata[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;

        if (cursor) {
          const item = cursor.value;

          // Apply filters to the item
          const isRaagDhalMatch =
            raagDhalFilter.length === 0 ||
            (item.rd && Array.isArray(item.rd)
              ? item.rd.some((rd: number) => raagDhalFilter.includes(rd)) // If item.rd is an array, check if any match
              : raagDhalFilter.includes(item.rd)); // If item.rd is a number, check if it exists in the filter

          const isBhavMatch =
            bhavFilter.length === 0 ||
            (item.bh && Array.isArray(item.bh)
              ? item.bh.some((bh: number) => bhavFilter.includes(bh)) // If item.bh is an array, check if any match
              : bhavFilter.includes(item.bh)); // If item.bh is a number, check if it exists in the filter

          const isRachaytaMatch = rachaytaFilter.length === 0 || rachaytaFilter.includes(item.r);

          // If the item matches any filters, add it to the result
          if (isRaagDhalMatch && isBhavMatch && isRachaytaMatch) {
            filteredData.push(item);
          }

          cursor.continue();
        } else {
          resolve(filteredData); // Resolve with the filtered data
        }
      };

      request.onerror = (event) => {
        reject(`Error fetching filtered data: ${(event.target as IDBRequest).error}`);
      };
    });
  },

  // Delete an item from any object store by its id
  deleteItem(id: number, storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("Database not initialized");
        return;
      }

      const transaction = this.db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error deleting item: ${(event.target as IDBRequest).error}`);
    });
  },

  // Clear all items from any object store
  clearObjectStore(storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("Database not initialized");
        return;
      }

      const transaction = this.db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error clearing object store: ${(event.target as IDBRequest).error}`);
    });
  },

  // Close the database connection
  closeDB(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  },
};

export default IndexedDBService;
